import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import {
  AVAILABLE_LANGUAGES,
  bookingResourceLanguage,
  normalizeBookingLanguage,
  PREFERRED_LANGUAGE_STORAGE_KEY,
  type BookingLanguageCode,
} from '../config/i18n';
import { syncPreferredLanguageToBackend } from '../utils/syncPreferredLanguage';

interface LanguageContextType {
  currentLanguage: BookingLanguageCode;
  changeLanguage: (lang: BookingLanguageCode) => Promise<void>;
  availableLanguages: typeof AVAILABLE_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<BookingLanguageCode>(() =>
    normalizeBookingLanguage(i18n.language)
  );

  useEffect(() => {
    const onChange = (lng: string) => {
      const normalized = normalizeBookingLanguage(lng);
      setCurrentLanguage(normalized);
      dayjs.locale(bookingResourceLanguage(normalized));
      document.documentElement.lang = normalized;
    };
    onChange(i18n.language);
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [i18n]);

  const changeLanguage = async (lang: BookingLanguageCode) => {
    await i18n.changeLanguage(lang);
    try {
      localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    void syncPreferredLanguageToBackend(lang);
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        availableLanguages: AVAILABLE_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
